import fetcher from '@/services/fetcher'
import type { Notification, Nullable } from '@/types'
import { toPortalUrl } from '@/utils/url-generation-utils'
import { defineStore } from 'pinia'

import { useUserStore } from './user'

type NotificationStoreState = {
  /**
   * Whether the notification dialog is currently shown.
   */
  show: boolean

  /**
   * A notification object containing required data to display.
   * If no `type` is provided in the notification, it will default to 'plain' styling,
   * else will use the type as the component.
   */
  notifications: Map<string, Notification>
}

export const useNotificationStore = defineStore('notification', {
  state: (): NotificationStoreState => ({
    show: false,
    notifications: new Map<string, Notification>(),
  }),

  getters: {
    /**
     * Returns the next notification to display.
     *
     * @returns {Nullable<Notification>}
     */
    nextNotification(): Nullable<Notification> {
      const notificationtKeys = Array.from(this.notifications.keys())

      if (!notificationtKeys.length) {
        return null
      }

      const nextNotificationtKey = notificationtKeys[0]

      return this.notifications.get(nextNotificationtKey) as Notification
    },
  },

  actions: {
    /**
     * Shows the next notification in the list.
     */
    showNotification() {
      if (!this.notifications.size) {
        return
      }

      const nextNotification = this.nextNotification
      if (
        nextNotification &&
        !this.getNotificationComponent(nextNotification)
      ) {
        console.warn(
          `No component registered for notification type: ${nextNotification.type}. Discarding.`
        )
        this.hideNotification(nextNotification.type, true, true)
        return
      }

      setTimeout(() => {
        this.show = true
      }, 100)
    },

    /**
     * Adds an notification dialog.
     *
     * @param {Notification} notificationDetails
     * @param {boolean} immediateShow
     */
    addNotification(
      notificationDetails: Notification,
      immediateShow: boolean = true
    ) {
      this.notifications.set(notificationDetails.type, notificationDetails)

      if (immediateShow) {
        this.showNotification()
      }
    },

    /**
     * Resets the notification state and hides the modal.
     *
     * @param {Notification['type']} notificationType
     * @param {boolean} markAsRead
     * @param {boolean} showNextNotification
     */
    hideNotification(
      notificationType?: Notification['type'],
      markAsRead: boolean = true,
      showNextNotification: boolean = true
    ) {
      this.show = false

      if (!this.notifications.size) {
        return
      }

      if (notificationType) {
        this.notifications.delete(notificationType)
      } else {
        const notificationKeys = Array.from(this.notifications.keys())
        const nextNotificationKey = notificationKeys[0]

        this.notifications.delete(nextNotificationKey)
      }

      if (markAsRead) {
        this.markNotificationAsRead()
      }

      if (showNextNotification) {
        this.showNotification()
      }
    },

    /**
     * Marks all notifications as read for the current user.
     *
     * @returns {Promise<boolean>}
     */
    async markNotificationAsRead() {
      const userStore = useUserStore()

      if (userStore.isLoggedOut) {
        console.error(
          'Cannot mark notification as read, user is not logged in.'
        )
        return
      }

      const { error } = await fetcher.get(
        toPortalUrl(`customers/notifications/read`)
      )

      if (error.value) {
        throw error.value
      }

      return true
    },

    /**
     * Returns the component name which should
     * be used to display the notification.
     *
     * @param {Notification} notificationDetails
     * @returns {Nullable<string>}
     */
    getNotificationComponent(
      notificationDetails: Notification
    ): Nullable<string> {
      switch (notificationDetails.type) {
        default:
          return null
      }
    },
  },
})
